import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { NomeLista, ElementoListaInsert } from '@/types/entities/lista-gestita.ts';

/**
 * wc-gestione-lista — Amministrazione di una lista gestita (colori delega, territori…).
 *
 * Attributi:
 *   lista         NomeLista (chiave di LISTE_GESTITE)
 *   data-options  JSON: { conCss: boolean } — mostra la colonna colore
 */
export class GestioneLista extends AppComponent {
  static override componentName = 'wc-gestione-lista';

  protected override get templateId() { return 'gestione-lista'; }

  private get lista(): NomeLista {
    return this.attributeString('lista') as NomeLista;
  }

  private get conCss(): boolean {
    return this.buildRenderingData().conCss === true;
  }

  protected override afterRender(): void {
    this.delegate.on('click', '[data-action="salva-elemento"]', (e: Event) => this.salva(e));
    this.delegate.on('click', '[data-action="elimina-elemento"]', (e: Event) => this.elimina(e));
    const form = this.$('[data-form="nuovo-elemento"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.crea(form);
      });
    }
    this.carica();
  }

  private async carica(): Promise<void> {
    const container = this.$('[data-righe]');
    if (!container) return;
    const result = await this.api.liste.lista(this.lista);
    const data = result.ok ? { elementi: result.data.elementi } : { error: result.error };
    templateParseToElement('gestione-lista-righe', container, { ...data, conCss: this.conCss });
  }

  /** Valori dei campi `data-field` di una riga o del form "Nuovo" */
  private leggiCampi(root: HTMLElement): Partial<ElementoListaInsert> {
    const campo = (nome: string) => root.querySelector(`[data-field="${nome}"]`) as HTMLInputElement | null;
    const dati: Partial<ElementoListaInsert> = {
      nome: campo('nome')?.value.trim() ?? '',
      ordine: Number(campo('ordine')?.value) || 0,
    };
    if (this.conCss) dati.css = campo('css')?.value ?? null;
    const valido = campo('valido');
    if (valido) dati.valido = valido.checked;
    return dati;
  }

  private async crea(form: HTMLFormElement): Promise<void> {
    const dati = this.leggiCampi(form);
    if (!dati.nome) {
      Weext.toast('Il nome è obbligatorio', { type: 'warning' });
      return;
    }
    const result = await this.api.liste.crea(this.lista, dati);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    form.reset();
    Weext.toast('Elemento aggiunto', { type: 'success' });
    this.carica();
  }

  private async salva(e: Event): Promise<void> {
    const riga = (e.target as HTMLElement).closest('[data-elemento]') as HTMLElement | null;
    if (!riga) return;
    const dati = this.leggiCampi(riga);
    if (!dati.nome) {
      Weext.toast('Il nome è obbligatorio', { type: 'warning' });
      return;
    }
    const result = await this.api.liste.aggiorna(this.lista, Number(riga.dataset.elemento), dati);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    Weext.toast('Elemento salvato', { type: 'success' });
    this.carica();
  }

  private async elimina(e: Event): Promise<void> {
    const riga = (e.target as HTMLElement).closest('[data-elemento]') as HTMLElement | null;
    if (!riga) return;
    const ok = await Weext.confirm('Eliminare l\'elemento selezionato?', {
      title: 'Conferma eliminazione',
      confirmText: 'Elimina',
      confirmClass: 'btn-danger',
      icon: 'bi-trash3-fill',
      iconClass: 'text-danger',
    });
    if (!ok) return;
    const result = await this.api.liste.elimina(this.lista, Number(riga.dataset.elemento));
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    Weext.toast('Elemento eliminato', { type: 'success' });
    this.carica();
  }
}
